/**
 * Cross-Page Navigator - Navigate between pages with context
 *
 * Provides functionality to:
 * - Navigate from main calculator to object finder
 * - Navigate from object finder to main calculator
 * - Pass object and search data between pages
 * - Maintain navigation history
 */

import { objectTypeLabel } from './displayNames.js';

export class CrossPageNavigator {
    constructor(calculator = null) {
        this.calculator = calculator;
        this.tempStorageKey = 'astro_temp_navigation_data';
    }

    /**
     * Navigate to Object Finder with pre-filled parameters
     * @param {Object} objectData - Object data from main calculator
     * @param {Object} params - Additional parameters
     */
    navigateToObjectFinder(objectData, params = {}) {
        try {
            // Calculate object's current position if calculator is available
            let position = null;
            if (this.calculator && objectData.ra_hours !== undefined && objectData.dec_degrees !== undefined) {
                const observationTime = params.observationDate ?
                    new Date(params.observationDate) : new Date();

                const altAz = this.calculator.raDecToAltAz(
                    objectData.ra_hours,
                    objectData.dec_degrees,
                    params.latitude || 42.0,
                    params.longitude || -6.0,
                    observationTime
                );

                position = {
                    azimuth: altAz.azimuth,
                    altitude: altAz.altitude
                };
            }

            // Build URL with parameters
            const urlParams = new URLSearchParams();

            if (position) {
                urlParams.set('az', position.azimuth.toFixed(1));
                urlParams.set('minAlt', Math.max(0, position.altitude - 20).toFixed(0));
                urlParams.set('maxAlt', Math.min(90, position.altitude + 20).toFixed(0));
            }

            if (params.latitude) urlParams.set('lat', params.latitude.toFixed(4));
            if (params.longitude) urlParams.set('lon', params.longitude.toFixed(4));
            if (params.observationDate) urlParams.set('date', params.observationDate);

            // Add source object name for context
            urlParams.set('source', 'calculator');
            if (objectData.name) {
                urlParams.set('sourceObj', encodeURIComponent(objectData.name));
            }

            const url = `/object-finder.html?${urlParams.toString()}`;
            window.location.href = url;
        } catch (error) {
            console.error('Failed to navigate to Object Finder:', error);
        }
    }

    /**
     * Navigate to Main Calculator with pre-filled object
     * @param {string} objectName - Object name or designation
     * @param {Object} params - Additional parameters
     */
    navigateToMainCalculator(objectName, params = {}) {
        try {
            const urlParams = new URLSearchParams();

            urlParams.set('object', encodeURIComponent(objectName));

            if (params.observationDate) {
                urlParams.set('date', params.observationDate);
            }

            if (params.latitude) urlParams.set('lat', params.latitude.toFixed(4));
            if (params.longitude) urlParams.set('lon', params.longitude.toFixed(4));

            urlParams.set('source', 'finder');

            const url = `/index.html?${urlParams.toString()}`;
            window.location.href = url;
        } catch (error) {
            console.error('Failed to navigate to Main Calculator:', error);
        }
    }

    /**
     * Store temporary navigation data in localStorage
     * @param {Object} data - Data to store
     */
    storeTemporaryData(data) {
        try {
            const tempData = {
                ...data,
                timestamp: new Date().toISOString(),
                expires: new Date(Date.now() + 3600000).toISOString() // 1 hour expiry
            };

            localStorage.setItem(this.tempStorageKey, JSON.stringify(tempData));
        } catch (error) {
            console.error('Failed to store temporary data:', error);
        }
    }

    /**
     * Retrieve temporary navigation data
     * @returns {Object|null} Temporary data or null if expired/not found
     */
    getTemporaryData() {
        try {
            const stored = localStorage.getItem(this.tempStorageKey);
            if (!stored) return null;

            const data = JSON.parse(stored);

            // Check if expired
            if (new Date(data.expires) < new Date()) {
                this.clearTemporaryData();
                return null;
            }

            return data;
        } catch (error) {
            console.error('Failed to retrieve temporary data:', error);
            return null;
        }
    }

    /**
     * Clear temporary navigation data
     */
    clearTemporaryData() {
        try {
            localStorage.removeItem(this.tempStorageKey);
        } catch (error) {
            console.error('Failed to clear temporary data:', error);
        }
    }

    /**
     * Get navigation source from URL
     * @returns {string|null} Source page or null
     */
    getNavigationSource() {
        const params = new URLSearchParams(window.location.search);
        return params.get('source');
    }

    /**
     * Get source object from URL
     * @returns {string|null} Source object name or null
     */
    getSourceObject() {
        const params = new URLSearchParams(window.location.search);
        const sourceObj = params.get('sourceObj');
        return sourceObj ? decodeURIComponent(sourceObj) : null;
    }

    /**
     * Create breadcrumb navigation data
     * @returns {Object} Breadcrumb data
     */
    getBreadcrumbData() {
        const source = this.getNavigationSource();
        const sourceObj = this.getSourceObject();

        if (!source) {
            return null;
        }

        const breadcrumb = {
            hasSource: true,
            sourcePage: source,
            sourceObject: sourceObj
        };

        if (source === 'calculator') {
            breadcrumb.backLabel = 'Back to Calculator';
            breadcrumb.backUrl = '/index.html';
            if (sourceObj) {
                breadcrumb.context = `Finding objects near ${sourceObj}`;
            }
        } else if (source === 'finder') {
            breadcrumb.backLabel = 'Back to Object Finder';
            breadcrumb.backUrl = '/object-finder.html';
            if (sourceObj) {
                breadcrumb.context = `Viewing ${sourceObj}`;
            }
        }

        return breadcrumb;
    }

    /**
     * Navigate back to previous page
     */
    navigateBack() {
        const breadcrumb = this.getBreadcrumbData();

        if (breadcrumb && breadcrumb.backUrl) {
            window.location.href = breadcrumb.backUrl;
        } else {
            window.history.back();
        }
    }

    /**
     * Open object finder for similar objects
     * @param {Object} targetObject - Reference object
     * @param {Object} searchParams - Search parameters
     */
    findSimilarObjects(targetObject, searchParams) {
        // Store detailed search criteria in temp storage
        this.storeTemporaryData({
            type: 'similarObjects',
            referenceObject: targetObject,
            criteria: {
                objectType: objectTypeLabel(targetObject),
                magnitudeRange: 2, // +/- 2 magnitudes
                ...searchParams
            }
        });

        // Navigate to object finder
        this.navigateToObjectFinder(targetObject, searchParams);
    }

    /**
     * Create a "Find Similar" link
     * @param {Object} object - Object data
     * @param {Object} params - Parameters
     * @returns {string} URL
     */
    createFindSimilarLink(object, params = {}) {
        const urlParams = new URLSearchParams();

        if (object.type) {
            urlParams.set('type', object.type);
        }

        if (object.magnitude !== undefined) {
            const minMag = Math.max(0, object.magnitude - 2);
            const maxMag = object.magnitude + 2;
            urlParams.set('minMag', minMag.toFixed(1));
            urlParams.set('maxMag', maxMag.toFixed(1));
        }

        if (params.latitude) urlParams.set('lat', params.latitude.toFixed(4));
        if (params.longitude) urlParams.set('lon', params.longitude.toFixed(4));

        urlParams.set('source', 'similar');
        if (object.name) {
            urlParams.set('sourceObj', encodeURIComponent(object.name));
        }

        return `/object-finder.html?${urlParams.toString()}`;
    }

    /**
     * Handle cross-page session continuity
     * @param {string} sessionId - Session identifier
     * @param {Object} sessionData - Session data
     */
    continueSession(sessionId, sessionData) {
        const sessionKey = `astro_session_${sessionId}`;

        try {
            localStorage.setItem(sessionKey, JSON.stringify({
                ...sessionData,
                timestamp: new Date().toISOString()
            }));
        } catch (error) {
            console.error('Failed to store session data:', error);
        }
    }

    /**
     * Retrieve session data
     * @param {string} sessionId - Session identifier
     * @returns {Object|null} Session data or null
     */
    getSessionData(sessionId) {
        const sessionKey = `astro_session_${sessionId}`;

        try {
            const stored = localStorage.getItem(sessionKey);
            return stored ? JSON.parse(stored) : null;
        } catch (error) {
            console.error('Failed to retrieve session data:', error);
            return null;
        }
    }

    /**
     * Clear session data
     * @param {string} sessionId - Session identifier
     */
    clearSession(sessionId) {
        const sessionKey = `astro_session_${sessionId}`;

        try {
            localStorage.removeItem(sessionKey);
        } catch (error) {
            console.error('Failed to clear session data:', error);
        }
    }
}

export default CrossPageNavigator;
